// Unabashedly stolen from RAIL (but uses a different Recoil Atom)
// Copy made instead of re-using component in case RAIL version changes in the future to use  wording that is specific to RAIL

import {
  CheckCircleOutlined,
  HourglassOutlined,
  IssuesCloseOutlined,
} from '@ant-design/icons';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { REQ_STATUS } from 'packages/lib/common-types/types';
import { saveToAudioApiStatusAtom } from 'packages/main/components/audio-tool/atoms';
import styled, { Keyframes, keyframes } from 'styled-components';
import { Tooltip } from 'antd';

const pulseGreen = keyframes`
	0% {
		box-shadow: 0 0 0 0 rgba(15, 225, 30, 0.8);
	}

	70% {
		box-shadow: 0 0 0 8px rgba(15, 225, 30, 0);
	}

	100% {
		box-shadow: 0 0 0 0 rgba(15, 225, 30, 0);
	}
`;
const pulseRed = keyframes`
	0% {
		box-shadow: 0 0 0 0 rgba(225, 15, 30, 0.8);
	}

	70% {
		box-shadow: 0 0 0 8px rgba(225, 15, 30, 0);
	}

	100% {
		box-shadow: 0 0 0 0 rgba(225, 15, 30, 0);
	}
`;

const AttentionContainer = styled.div<{ hidePulse: boolean; error?: boolean }>`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 40px;
  max-height: 40px;
  border-radius: 0.15rem;
  ${(props): string =>
    !props.hidePulse
      ? `border: 2px solid ${
          props.error ? 'rgba(220, 0, 40)' : 'rgba(0, 220, 40)'
        };`
      : 'border: 2px solid #CCC;'}
  ${(props): string => (!props.hidePulse ? '' : 'box-shadow: none !important;')}
  animation: ${(props): Keyframes =>
    props.error ? pulseRed : pulseGreen} 1s infinite;
`;

const TickerMessage = ({ d }: { d: Date | null }): JSX.Element => {
  const isMounted = useRef(false);
  const [now, setNow] = useState<Date | null>(d);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    setTimeout(() => {
      if (isMounted.current) {
        setNow(new Date());
      }
    }, 1000);
  }, [d, now]);
  if (!now || !d) {
    return <span>No changes</span>;
  }
  const diffMs = Math.abs(now.valueOf() - d.valueOf());
  const minutes = Math.floor(diffMs / 1000 / 60);
  return (
    <span>{`Last saved ${minutes < 1 ? '<1' : minutes} minute${
      minutes < 2 ? '' : 's'
    } ago`}</span>
  );
};

export const SaveStateIndicator = (): JSX.Element => {
  const isMounted = useRef(false);
  const saveState = useRecoilValue(saveToAudioApiStatusAtom);
  const previousSaveState = useRef(saveState);
  const [hideSuccess, setHideSuccess] = useState(true); // hides the 'Success' message after 2 seconds
  const [hidePulse, setHidePulse] = useState(true);
  const lastSavedRef = useRef<Date | null>(null);
  useLayoutEffect(() => {
    if (saveState !== previousSaveState.current) {
      previousSaveState.current = saveState;
      if (saveState === REQ_STATUS.success) {
        lastSavedRef.current = new Date();
        setHideSuccess(false);
        setTimeout(() => {
          if (isMounted.current) {
            setHideSuccess(true);
          }
        }, 750);
        setTimeout(() => {
          if (isMounted.current) {
            setHidePulse(true);
          }
        }, 4150);
      } else {
        setHidePulse(false);
      }
    }
  }, [saveState]);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return (
    <div
      data-testid="save-state-indicator"
      style={{
        display: 'flex',
        width: '120px',
        textAlign: 'center',
      }}
    >
      <Tooltip
        data-testid="save-indicator-tooltip"
        title={
          saveState === REQ_STATUS.error ? (
            <span>
              There was a problem saving.
              <br />
              <br />
              To report this error, please refresh your browser, record your
              screen while repeating the steps that caused the error, and submit
              a bug ticket with your video.
            </span>
          ) : (
            <span>Your work is saved automatically when you make changes.</span>
          )
        }
      >
        <AttentionContainer
          hidePulse={hidePulse}
          error={saveState === REQ_STATUS.error}
        >
          {saveState === REQ_STATUS.pending && (
            <div
              data-testid="save-indicator-pending"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <HourglassOutlined
                style={{
                  margin: 6,
                  marginRight: 4,
                  fontSize: '18px',
                  color: '#52c41a',
                }}
                twoToneColor="#52c41a"
              />
              Saving...
            </div>
          )}
          {(saveState === REQ_STATUS.success || saveState === null) &&
            hideSuccess && (
              <div
                data-testid="save-indicator-success"
                style={{
                  color: '#333',
                  margin: 'auto',
                  lineHeight: '14px',
                }}
              >
                <TickerMessage d={lastSavedRef.current} />
              </div>
            )}
          {saveState === REQ_STATUS.success && !hideSuccess && (
            <div
              data-testid="save-indicator-success"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <CheckCircleOutlined
                style={{ margin: 6, fontSize: '18px', color: '#52c41a' }}
                twoToneColor="#52c41a"
              />
              Saved !
            </div>
          )}
          {saveState === REQ_STATUS.error && (
            <div
              data-testid="save-indicator-error"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <IssuesCloseOutlined
                style={{ margin: 10, fontSize: '18px', color: '#c4521a' }}
                twoToneColor="#c4521a"
              />
              Error.
            </div>
          )}
        </AttentionContainer>
      </Tooltip>
    </div>
  );
};
